
import { useState, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Turnstile, TurnstileInstance } from '@marsidev/react-turnstile'
import { Loader2 } from 'lucide-react'
import { toast } from 'react-hot-toast'
import * as env from '@/env'

interface ContactProps {
	prefillEmail?: string
}

export default function Contact({ prefillEmail }: ContactProps) {
	const devTurnstileSiteKey = '1x00000000000000000000AA'
	const stagingTurnstileSiteKey = '0x4AAAAAAAklWnwo0zUOtnIF'
	const prodTurnstileSiteKey = '0x4AAAAAACJsRn4g6KePolaM'

	let turnstileSiteKey = ''
	if (env.isDevelopment)
		turnstileSiteKey = devTurnstileSiteKey
	else if (env.isStaging)
		turnstileSiteKey = stagingTurnstileSiteKey
	else
		turnstileSiteKey = prodTurnstileSiteKey

	const [email, setEmail] = useState(prefillEmail ?? '')
	const [subject, setSubject] = useState('')
	const [message, setMessage] = useState('')
	const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
	const [isLoading, setIsLoading] = useState(false)
	const turnstileRef = useRef<TurnstileInstance>(null)

	const isSendEnabled = email.trim() !== '' && subject.trim() !== '' && message.trim() !== '' && turnstileToken !== null && !isLoading

	const handleSend = async () => {
		setIsLoading(true)
		try {
			const response = await fetch(
				`${env.restBaseUrl}/api/v1/contact`,
				{
					method: 'POST',
					credentials: 'include',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ email, subject, message, turnstileToken }),
				}
			)
			const data = await response.json()
			if (!response.ok)
				throw new Error(data.message)
			toast.success('Message sent!')
			setEmail(prefillEmail ?? '')
			setSubject('')
			setMessage('')
			setTurnstileToken(null)
			turnstileRef.current?.reset()
		} catch (error) {
			toast.error('Failed to send message. Please try again later.')
			console.error('Error sending contact form:', error)
		} finally {
			setIsLoading(false)
		}
	}

	return (
		<div className="space-y-4">
			<h1 className="scroll-m-20 text-4xl font-bold tracking-tight">Contact</h1>

			<Input
				type="email"
				maxLength={254}
				placeholder="your@email.com"
				value={email}
				onChange={(e) => setEmail(e.target.value)}
			/>
			<Input
				type="text"
				maxLength={100}
				placeholder="Subject"
				value={subject}
				onChange={(e) => setSubject(e.target.value)}
			/>
			<Textarea
				maxLength={2000}
				placeholder="Your message..."
				value={message}
				onChange={(e) => setMessage(e.target.value)}
				className="min-h-[120px]"
			/>
			<div className="flex justify-center">
				<Turnstile
					ref={turnstileRef}
					siteKey={turnstileSiteKey}
					onSuccess={(token) => setTurnstileToken(token)}
					onError={() => setTurnstileToken(null)}
					onExpire={() => setTurnstileToken(null)}
				/>
			</div>
			<div className="flex justify-end">
				<Button
					onClick={handleSend}
					disabled={!isSendEnabled}
					className="bg-green-500 hover:bg-green-600 text-white flex items-center justify-center w-32"
				>
					{isLoading ? (
						<Loader2 className="h-4 w-4 animate-spin" />
					) : (
						'Send'
					)}
				</Button>
			</div>
		</div>
	)
}
